import { useEffect, useRef, useState, useCallback } from 'react';
import { FiSearch, FiEye, FiFileText, FiCalendar, FiUser, FiActivity, FiCheckCircle, FiDownload, FiExternalLink, FiFilter, FiX } from 'react-icons/fi';
import { BsFilePdf } from 'react-icons/bs';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';
import ExamDetailModal from '../components/ExamDetailModal';
import Pagination from '../components/Pagination';
import { useScopedSiteFilter } from '../hooks/useScopedSiteFilter';
import useUser from '../hooks/useUser';
import { CRUD_UI } from '../components/crudUi';

const EXAM_LINK_OPEN_MODE = (import.meta.env.VITE_EXAM_LINK_OPEN_MODE || 'tab').toLowerCase();
const shouldOpenExamLinkInNewTab = EXAM_LINK_OPEN_MODE === 'tab';

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [sites, setSites] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [iframeModal, setIframeModal] = useState(null);
  const iframeRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { applySiteScope } = useScopedSiteFilter();
  const { user } = useUser();
  const canViewAllExamSites = user?.role === 'LocalUser' || Boolean(user?.canAccessAllSites);

  const fetchSites = async () => {
    try {
      const response = await api.get('/orthanc/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchModalities = async () => {
    try {
      let params = {};
      if (canViewAllExamSites) {
        if (filterSite) {
          params.site_id = filterSite;
        }
      } else {
        params = applySiteScope(params, filterSite);
      }

      const response = await api.get('/exams/modalities', { params });
      setModalities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching exam modalities:', error);
      setModalities([]);
    }
  };

  const fetchExams = async () => {
    try {
      let params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterModality) params.modality = filterModality;
      if (canViewAllExamSites) {
        if (filterSite) {
          params.site_id = filterSite;
        }
      } else {
        params = applySiteScope(params, filterSite);
      }
      
      const response = await api.get('/exams', { params });
      setExams(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Không thể tải danh sách ca chụp');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    fetchModalities();
  }, [filterSite, canViewAllExamSites]);

  useEffect(() => {
    if (filterModality && modalities.length > 0 && !modalities.includes(filterModality)) {
      setCurrentPage(1);
      setFilterModality('');
    }
  }, [modalities, filterModality]);

  useEffect(() => {
    fetchExams();
  }, [currentPage, itemsPerPage, search, filterStatus, filterModality, filterSite]);

  useEffect(() => {
    if (!iframeModal) {
      return undefined;
    }

    const handleIframeMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data;
      const nextUrl = typeof data === 'string'
        ? data
        : data?.url || data?.href || data?.currentUrl;

      if (!nextUrl || typeof nextUrl !== 'string') {
        return;
      }

      console.log('[Exams] Iframe postMessage URL:', {
        origin: event.origin,
        url: nextUrl,
        data,
      });

      setIframeModal((current) => {
        if (!current || current.objectUrl || current.url === nextUrl) {
          return current;
        }

        return {
          ...current,
          url: nextUrl,
        };
      });
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [iframeModal]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Đã lên lịch', bg: 'bg-blue-100', text: 'text-blue-700' },
      in_progress: { label: 'Đang thực hiện', bg: 'bg-amber-100', text: 'text-amber-700' },
      completed: { label: 'Hoàn thành', bg: 'bg-emerald-100', text: 'text-emerald-700' },
      cancelled: { label: 'Đã hủy', bg: 'bg-red-100', text: 'text-red-700' },
      synced: { label: 'Đã đồng bộ', bg: 'bg-indigo-100', text: 'text-indigo-700' },
    };
    const config = statusConfig[status] || { label: status, bg: 'bg-blue-100', text: 'text-blue-700' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  // Handle items per page change - use useCallback to ensure stable reference
  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
        <p className="text-slate-500 text-sm">Đang tải danh sách ca chụp...</p>
      </div>
    );
  }

  // Get DICOM viewer URL from environment variable
  const getDicomViewerUrl = (studyInstanceUID) => {
    if (!studyInstanceUID) return null;
    
    // Get viewer URL from env (Vite exposes VITE_* vars)
    // Check both import.meta.env (runtime) and process.env (build time)
    const viewerBaseUrl = import.meta.env.VITE_DICOM_VIEWER_URL || 
                          import.meta.env.DICOM_VIEWER_URL ||
                          'http://localhost:8042/ohif/viewer';
    
    // Debug logging
    console.log('DICOM Viewer Configuration:', {
      'import.meta.env.VITE_DICOM_VIEWER_URL': import.meta.env.VITE_DICOM_VIEWER_URL,
      'import.meta.env.MODE': import.meta.env.MODE,
      'viewerBaseUrl': viewerBaseUrl,
      'studyInstanceUID': studyInstanceUID
    });
    
    // Remove trailing slash if exists
    const baseUrl = viewerBaseUrl.replace(/\/$/, '');
    
    // Construct URL with StudyInstanceUID parameter
    const finalUrl = `${baseUrl}?StudyInstanceUIDs=${encodeURIComponent(studyInstanceUID)}`;
    
    return finalUrl;
  };

  const handleRowClick = async (exam, e) => {
    // Don't trigger if clicking on action buttons or links
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }

    // Only open viewer if study_instance_uid exists
    if (!exam.study_instance_uid) {
      toast.info('Ca chụp chưa có Study Instance UID');
      return;
    }

    // Check if study exists in Orthanc before opening viewer
    try {
      //toast.info('Đang kiểm tra study trong Orthanc...', { autoClose: 2000 });
      
      const checkResponse = await api.post('/orthanc/check-study', {
        study_instance_uid: exam.study_instance_uid,
      });

      if (checkResponse.data.success && checkResponse.data.data.exists) {
        // Study exists, open viewer
        const viewerUrl = getDicomViewerUrl(exam.study_instance_uid);
        if (viewerUrl) {
          console.log('Opening DICOM viewer:', viewerUrl);
          console.log('[Exams] Open row DICOM iframe URL:', viewerUrl);

          if (shouldOpenExamLinkInNewTab) {
            window.open(viewerUrl, '_blank', 'noopener,noreferrer');
            return;
          }

          setIframeModal({
            title: 'Xem ảnh DICOM',
            url: viewerUrl,
            objectUrl: null,
          });
          //toast.success('Đang mở DICOM viewer...', { autoClose: 2000 });
        } else {
          toast.warning('Không thể tạo URL viewer');
        }
      } else {
        // Study not found in Orthanc
        toast.warning(
          //'Study chưa có trong Orthanc. Vui lòng đợi modality gửi ảnh về Orthanc.',
          'Ca chụp chưa có kết quả. Vui lòng đợi hoặc liên hệ với LocalUser.',
          { autoClose: 5000 }
        );
      }
    } catch (error) {
      console.error('Error checking study:', error);
      const errorMessage = error.response?.data?.error?.message || 'Không thể kiểm tra study trong Orthanc';
      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  const openResultViewer = (exam) => {
    const viewerUrl = exam.result_viewer_url || exam.viewer_url;
    if (!viewerUrl) {
      toast.info('Ca chụp chưa có link DICOM viewer');
      return;
    }

    console.log('[Exams] Open result DICOM iframe URL:', viewerUrl);

    if (shouldOpenExamLinkInNewTab) {
      window.open(viewerUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setIframeModal({
      title: 'Xem ảnh DICOM',
      url: viewerUrl,
      objectUrl: null,
    });
  };

  const closeIframeModal = () => {
    if (iframeModal?.objectUrl) {
      URL.revokeObjectURL(iframeModal.objectUrl);
    }
    setIframeModal(null);
  };

  const openResultPdf = async (exam) => {
    const pdfUrl = exam.result_pdf_view_url || exam.result_pdf_url;

    if (!pdfUrl) {
      toast.info('Ca chụp chưa có file PDF kết quả');
      return;
    }

    if (shouldOpenExamLinkInNewTab) {
      if (exam.result_pdf_view_url && exam.result_pdf_view_url !== exam.result_pdf_url) {
        const pdfTab = window.open('', '_blank', 'noopener,noreferrer');

        try {
          console.log('[Exams] Fetch PDF for new tab URL:', exam.result_pdf_view_url);
          const response = await api.get(exam.result_pdf_view_url, {
            responseType: 'blob',
          });
          const objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          if (pdfTab) {
            pdfTab.location.href = objectUrl;
          } else {
            window.open(objectUrl, '_blank', 'noopener,noreferrer');
          }
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
          return;
        } catch (error) {
          pdfTab?.close();
          toast.error('Không thể tải file PDF kết quả');
          return;
        }
      }

      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (exam.result_pdf_view_url && exam.result_pdf_view_url !== exam.result_pdf_url) {
      try {
        console.log('[Exams] Fetch PDF for iframe URL:', exam.result_pdf_view_url);
        const response = await api.get(exam.result_pdf_view_url, {
          responseType: 'blob',
        });
        const objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        console.log('[Exams] Open PDF iframe blob URL:', objectUrl);

        setIframeModal({
          title: 'Xem PDF kết quả',
          url: objectUrl,
          objectUrl,
        });
        return;
      } catch (error) {
        toast.error('Không thể tải file PDF kết quả');
        return;
      }
    }

    console.log('[Exams] Open PDF iframe URL:', pdfUrl);
    setIframeModal({
      title: 'Xem PDF kết quả',
      url: pdfUrl,
      objectUrl: null,
    });
  };

  const handleIframeLoad = (event) => {
    try {
      const nextUrl = event.currentTarget.contentWindow?.location?.href;
      console.log('[Exams] Iframe loaded URL:', nextUrl);

      if (!nextUrl) {
        return;
      }

      setIframeModal((current) => {
        if (!current || current.objectUrl || current.url === nextUrl) {
          return current;
        }

        return {
          ...current,
          url: nextUrl,
        };
      });
    } catch (error) {
      console.log('[Exams] Cannot read iframe URL after load:', {
        currentUrl: iframeModal?.url,
        reason: error?.message || String(error),
      });
      // Cross-origin iframes cannot expose their final URL to the parent page.
    }
  };

  return (
    <div className={CRUD_UI.page}>
      {/* Search and Filters */}
      <div className={CRUD_UI.filterCard}>
        <div className={CRUD_UI.filterHeader}>
          <FiFilter className="text-slate-500 text-xl" />
          <span className="text-sm font-semibold text-slate-900">Tìm kiếm và bộ lọc</span>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm kiếm</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Tìm theo tên bệnh nhân, mã bệnh nhân, mã chỉ định, cơ sở..."
                value={search}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearch(e.target.value);
                }}
              />
              {search && (
                <button
                  className={CRUD_UI.searchClearButton}
                  onClick={() => {
                    setCurrentPage(1);
                    setSearch('');
                  }}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Trạng thái</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterStatus}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterStatus(e.target.value);
              }}
            >
              <option value="">Tất cả trạng thái</option>
              <option value="scheduled">Đã lên lịch</option>
              <option value="in_progress">Đang thực hiện</option>
              <option value="completed">Hoàn thành</option>
              <option value="cancelled">Đã hủy</option>
              <option value="synced">Đã đồng bộ</option>
            </select>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Loại máy</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterModality}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterModality(e.target.value);
              }}
            >
              <option value="">Tất cả loại máy</option>
              {modalities.map((modality) => (
                <option key={modality} value={modality}>
                  {modality}
                </option>
              ))}
            </select>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Cơ sở</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterSite}
              onChange={(e) => {
                setCurrentPage(1);
                setFilterSite(e.target.value);
              }}
            >
              <option value="">Tất cả cơ sở</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className="w-full border-collapse bg-white" key={`exams-table-${currentPage}-${itemsPerPage}`}>
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <FiUser className="text-base" />
                    Tên bệnh nhân
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Mã bệnh nhân</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Accession Number</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <FiActivity className="text-base" />
                    Loại máy
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Trạng thái</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <FiCalendar className="text-base" />
                    Thời gian lên lịch
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Cơ sở</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Máy chụp</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Description</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Thao tác</th>
              </tr>
            </thead>
            <tbody key={`page-${currentPage}-items-${itemsPerPage}`}>
              {exams.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-3 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FiFileText className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Không tìm thấy ca chụp nào</p>
                    </div>
                  </td>
                </tr>
              ) : (
                exams.map((exam) => (
                  <tr 
                    key={exam.id}
                    className={`gpacs-table-row border-t border-slate-200 transition-colors hover:bg-slate-100 ${exam.study_instance_uid ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={(e) => handleRowClick(exam, e)}
                  >
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <strong className="text-slate-900">{exam.patient_name || '-'}</strong>
                        {exam.study_instance_uid && (
                          <FiExternalLink className="text-primary text-base" title="Click để mở DICOM Viewer" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{exam.patient_id || '-'}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{exam.accession_number || '-'}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{exam.modality || '-'}</span>
                    </td>
                    <td className="px-3 py-2 text-sm">{getStatusBadge(exam.status)}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {formatDateTime(exam.scheduled_time)}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{exam.site_name || '-'}</td>
                    <td className="px-3 py-2 text-sm">
                      {exam.station_name ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-slate-700">{exam.station_name}</span>
                          {exam.station_ae_title && (
                            <code className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{exam.station_ae_title}</code>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700 max-w-[220px]">
                      <span className="line-clamp-2" title={exam.description || ''}>{exam.description || '-'}</span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-primary hover:bg-primary hover:text-white"
                          title="Xem chi tiết"
                          aria-label="Xem chi tiết"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedExamId(exam.id);
                          }}
                        >
                          <FiEye className="text-base" />
                        </button>
                        <button
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-primary hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${(exam.result_viewer_url || exam.viewer_url) ? '' : 'opacity-70'}`}
                          disabled={!(exam.result_viewer_url || exam.viewer_url)}
                          title={(exam.result_viewer_url || exam.viewer_url) ? 'Mở DICOM viewer' : 'Chưa có viewer_url'}
                          aria-label="Xem Dicom"
                          onClick={(e) => {
                            e.stopPropagation();
                            openResultViewer(exam);
                          }}
                        >
                          <FiExternalLink className="text-base" />
                        </button>
                        <button
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-primary hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${exam.result_pdf_view_url || exam.result_pdf_url ? '' : 'opacity-70'}`}
                          disabled={!(exam.result_pdf_view_url || exam.result_pdf_url)}
                          title={exam.result_pdf_view_url || exam.result_pdf_url ? 'Mở file PDF kết quả' : 'Chưa có PDF'}
                          aria-label="Xem PDF"
                          onClick={(e) => {
                            e.stopPropagation();
                            openResultPdf(exam);
                          }}
                        >
                          <BsFilePdf className="text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {selectedExamId && (
        <ExamDetailModal
          examId={selectedExamId}
          onClose={() => setSelectedExamId(null)}
          onRefresh={fetchExams}
        />
      )}

      {iframeModal && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-3 sm:p-5"
          onClick={closeIframeModal}
        >
          <div
            className="flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="truncate pr-4 text-base font-semibold text-slate-900">{iframeModal.title}</h3>
              <button
                className="shrink-0 rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                onClick={closeIframeModal}
                aria-label="Đóng popup"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <iframe
              ref={iframeRef}
              src={iframeModal.url}
              title={iframeModal.title}
              className="min-h-0 flex-1 border-0"
              allow="fullscreen"
              allowFullScreen
              onLoad={handleIframeLoad}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Exams;

